import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sales, Delivery, apiErrorMessage } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft, Printer, Download, FileText, AlertTriangle, Loader2, ExternalLink,
} from "lucide-react";

/**
 * Renders the server-merged delivery file PDF in a single iframe and exposes
 * Print / Download / Open in New Tab actions. Pre-flights for missing
 * mandatory documents and lets the user Continue / Cancel.
 */
export default function PrintDeliveryFile() {
  const { saleId } = useParams();
  const navigate = useNavigate();

  const [sale, setSale] = useState(null);
  const [pre, setPre] = useState(null);          // preflight result
  const [pdfUrl, setPdfUrl] = useState("");      // object URL of the rendered PDF
  const [phase, setPhase] = useState("preflight");
  // phase: "preflight" | "confirm-missing" | "preparing" | "ready" | "error"
  const [error, setError] = useState("");
  const blobRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          Sales.get(saleId),
          Delivery.preflight(saleId),
        ]);
        setSale(s); setPre(p);
        if (p.missing_mandatory.length > 0) {
          setPhase("confirm-missing");
        } else {
          await generate();
        }
      } catch (e) {
        setError(apiErrorMessage(e) || "Could not load delivery file");
        setPhase("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleId]);

  const generate = useCallback(async () => {
    setPhase("preparing");
    setError("");
    try {
      const blob = await Delivery.pdf(saleId);
      blobRef.current = blob;
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPhase("ready");
    } catch (e) {
      setError(apiErrorMessage(e) || "Failed to build the delivery file");
      setPhase("error");
    }
  }, [saleId]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const filename = sale?.sr_number
    ? `Delivery_File_${sale.sr_number}.pdf`
    : `Delivery_File_${saleId}.pdf`;

  const onDownload = () => {
    if (!blobRef.current) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onOpenNewTab = () => {
    if (pdfUrl) window.open(pdfUrl, "_blank");
  };

  const onPrint = () => {
    // Best-effort: focus the embedded iframe and call print on its window.
    const iframe = document.getElementById("delivery-file-iframe");
    try {
      iframe?.contentWindow?.focus();
      iframe?.contentWindow?.print();
    } catch {
      // Fallback — open in a new tab and let the user hit Ctrl+P there.
      onOpenNewTab();
    }
  };

  // ── Confirm-missing dialog ────────────────────────────────────────────
  if (phase === "confirm-missing" && pre) {
    return (
      <Dialog open onOpenChange={() => navigate(-1)}>
        <DialogContent className="sm:max-w-md" data-testid="delivery-confirm-missing">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Some mandatory documents are pending
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              The following documents have not been uploaded yet. You can still print
              a cover + checklist now and add the documents later.
            </p>
            <ul className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="missing-doc-list">
              {pre.missing_mandatory.map((title) => (
                <li key={title} className="flex items-center gap-2 py-0.5 text-amber-900">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  {title}
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500">
              {pre.available_count} document{pre.available_count === 1 ? "" : "s"} ready to merge.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => navigate(-1)} data-testid="delivery-cancel-btn">
              Cancel
            </Button>
            <Button onClick={generate}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="delivery-continue-btn">
              Continue Printing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="space-y-4" data-testid="print-delivery-file-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate(`/delivery-files/${saleId}`)}
            className="mb-1 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
            data-testid="delivery-back-btn"
          >
            <ArrowLeft className="h-3 w-3" /> Back to Delivery File
          </button>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Print Delivery File</h1>
          {sale && (
            <p className="text-xs text-slate-500">
              <span className="font-mono-data font-semibold text-slate-900">{sale.sr_number}</span>
              {" · "}{sale.customer?.name}
              {" · "}{sale.vehicle?.model}
              {sale.vehicle?.vin && <> · VIN <span className="font-mono-data">{sale.vehicle.vin}</span></>}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="gap-1.5"
                  onClick={onOpenNewTab}
                  disabled={phase !== "ready"}
                  data-testid="delivery-open-btn">
            <ExternalLink className="h-4 w-4" /> Open
          </Button>
          <Button variant="outline" className="gap-1.5"
                  onClick={onDownload}
                  disabled={phase !== "ready"}
                  data-testid="delivery-download-btn">
            <Download className="h-4 w-4" /> Download
          </Button>
          <Button className="gap-1.5"
                  onClick={onPrint}
                  disabled={phase !== "ready"}
                  data-testid="delivery-print-btn">
            <Printer className="h-4 w-4" /> Print
          </Button>
        </div>
      </div>

      {/* Body */}
      {phase === "preparing" || phase === "preflight" ? (
        <Card className="flex flex-col items-center justify-center gap-3 border-slate-200 px-6 py-20 text-center"
              data-testid="delivery-preparing-card">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="font-display text-lg font-semibold text-slate-800">
            Preparing Delivery File…
          </div>
          <p className="max-w-md text-sm text-slate-500">
            Collecting documents, merging into a single PDF and stamping the header on every page.
            This usually takes 2–5 seconds.
          </p>
        </Card>
      ) : phase === "error" ? (
        <Card className="border-rose-200 bg-rose-50/40 p-6 text-sm text-rose-800"
              data-testid="delivery-error-card">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4" /> Failed to build delivery file
          </div>
          <p className="mt-1">{error}</p>
          <Button className="mt-3" onClick={generate} variant="outline">Try again</Button>
        </Card>
      ) : phase === "ready" ? (
        <Card className="overflow-hidden border-slate-200 p-0" data-testid="delivery-pdf-card">
          <iframe
            id="delivery-file-iframe"
            data-testid="delivery-file-iframe"
            title="Delivery File"
            src={pdfUrl}
            className="h-[calc(100vh-220px)] w-full border-0"
          />
        </Card>
      ) : null}

      {pre && phase === "ready" && pre.missing_mandatory.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
             data-testid="delivery-missing-banner">
          <FileText className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
          Printed with missing mandatory documents: <strong>{pre.missing_mandatory.join(", ")}</strong>
        </div>
      )}
    </div>
  );
}
